import React, { MouseEventHandler, PropsWithChildren, ReactElement, ReactNode } from 'react'
import clsx from 'clsx'
import { Chip as ChipStyles } from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import useDeprecatedProps from '../../lib/hooks/useDeprecatedProps'
import { CommonProps } from '../helperTypes'

type Props = PropsWithChildren<{
  /** If true, the chip will appear clickable. */
  clickable?: boolean
  /** An icon to display. */
  icon?: ReactElement
  /** The label of the `Chip`. */
  label?: string | ReactNode
  /** Callback function fired when the delete button is clicked. If set, the delete button will be shown. */
  onDelete?: MouseEventHandler<HTMLButtonElement>
  /** Callback function fired when the chip is clicked. If set, the chip will appear clickable. */
  onClick?: MouseEventHandler<HTMLDivElement>
  /**
   * The size of the chip.
   * @default small
   */
  size?: 'medium' | 'small' | 'xsmall'
  /**
   * The variant of the chip.
   * @default default
   */
  variant?: 'default' | 'info' | 'success' | 'warning' | 'error'
}>

type DeprecatedProps = {
  disabled?: boolean
  primary?: boolean
  secondary?: boolean
}

export type ChipProps = Props & DeprecatedProps & CommonProps

const handleDeprecatedProps = (props: ChipProps): ChipProps => {
  const { disabled, primary, secondary, ...rest } = props
  return rest
}

const useStyles = createUseStyles(ChipStyles, 'chip')

const DeleteButton = ({ onClick }: { onClick: MouseEventHandler<HTMLButtonElement> }) => {
  const classes = useStyles()
  return (
    <button
      type='button'
      className={classes.removeBtn}
      aria-label='Remove'
      onClick={onClick}
      onTouchEnd={(e: any) => {
        e.preventDefault()
        onClick && onClick(e)
      }}
      onMouseDown={(e) => {
        e.preventDefault()
        e.stopPropagation()
      }}
    />
  )
}

const Chip = (props: ChipProps) => {
  const {
    className,
    label,
    children,
    icon,
    onDelete,
    onClick,
    clickable,
    variant,
    size = 'small',
    ...rest
  } = handleDeprecatedProps(props)
  useDeprecatedProps('Chip', { disabled: undefined, primary: undefined, secondary: undefined }, props)

  const classes = useStyles()

  return (
    <div
      role={clickable || onClick ? 'button' : undefined}
      onClick={onClick}
      className={clsx(
        className,
        classes.root,
        (clickable || onClick) && classes._clickable,
        variant !== 'default' && classes[`_${variant}`],
        size !== 'small' && classes[`_${size}`],
      )}
      {...rest}
    >
      {icon && <span className={classes.icon}>{icon}</span>}
      <span className={classes.label}>{label || children}</span>
      {onDelete && <DeleteButton onClick={onDelete} />}
    </div>
  )
}

export default Chip
